import { useEffect, useState } from 'react';
import { useTourContext } from '@/store/tourContext';
import type { TourElementKey } from '@/utils/tutorials';
import { tutorialActions } from '@/utils/tutorials';
import { useTutorialActive } from './useTutorialActive';

// Shared state for controlling create menu during tutorials
// This is accessed by DataTableFilters component
let shouldForceOpenCreateMenuForTutorial = false;

export const getForceOpenCreateMenuForTutorial = (): boolean => {
	return shouldForceOpenCreateMenuForTutorial;
};

export const setForceOpenCreateMenuForTutorial = (force: boolean): void => {
	shouldForceOpenCreateMenuForTutorial = force;
};

interface UseRequestsTutorialProps {
	requestsTableRef: React.RefObject<HTMLDivElement | null>;
	ordersActionHamburgerRef: React.RefObject<HTMLElement | null>;
}

/**
 * Custom hook to manage all Requests tutorial logic
 * Handles tutorial actions, ref registrations
 */
/**
 * Helper function to find the create menu
 */
const findCreateMenuBox = (): HTMLElement | null => {
	const byStyle = document.querySelector('[style*="z-index: 1500"]') as HTMLElement;
	if (byStyle) {
		const style = window.getComputedStyle(byStyle);
		if (parseInt(style.zIndex) >= 1500) {
			return byStyle;
		}
	}
	
	const allDivs = document.querySelectorAll('div');
	for (const div of Array.from(allDivs)) {
		const style = window.getComputedStyle(div);
		const zIndex = parseInt(style.zIndex || '0', 10);
		if (zIndex >= 1500 && style.position === 'fixed') {
			const text = div.textContent || '';
			if (text.includes('Patient Order') || text.includes('Clinic Order')) {
				return div as HTMLElement;
			}
		}
	}
	
	return null;
};

/**
 * Helper function to find menu buttons
 */
const findMenuButtons = (container: HTMLElement | Document = document): {
	singleOrderButton: HTMLElement | null;
	bulkOrderButton: HTMLElement | null;
} => {
	let singleOrderButton: HTMLElement | null = null;
	let bulkOrderButton: HTMLElement | null = null;

	const elements = container === document 
		? document.querySelectorAll('*')
		: container.querySelectorAll('*');

	for (const element of Array.from(elements)) {
		if (singleOrderButton && bulkOrderButton) break;

		const text = element.textContent?.trim() || '';
		const htmlElement = element as HTMLElement;
		const style = window.getComputedStyle(htmlElement);
		const rect = htmlElement.getBoundingClientRect();

		const isVisible =
			style.display !== 'none' &&
			style.visibility !== 'hidden' &&
			rect.width > 0 &&
			rect.height > 0;

		if (!isVisible) continue;

		const isClickableBox = 
			style.cursor === 'pointer' && 
			(style.display === 'flex' || style.display === 'block');

		// Check for Patient Order button
		if (!singleOrderButton && (text.includes('Patient Order') || text === 'Create Patient Order')) {
			if (isClickableBox || !singleOrderButton) {
				singleOrderButton = htmlElement;
			}
		}
		
		// Check for Clinic Order button
		if (!bulkOrderButton && (text.includes('Clinic Order') || text === 'Create Clinic Order')) {
			if (isClickableBox || !bulkOrderButton) {
				bulkOrderButton = htmlElement;
			}
		}
	}

	return { singleOrderButton, bulkOrderButton };
};

export const useRequestsTutorial = ({
	requestsTableRef,
	ordersActionHamburgerRef,
}: UseRequestsTutorialProps): void => {
	const { registerElement } = useTourContext();
	const { isRequestsTutorialActive } = useTutorialActive();

	// Register expandActionColumn action
	useEffect(() => {
		// Action to expand action column (for viewing order details)
		tutorialActions.register('expandActionColumn', () => {
			if (isRequestsTutorialActive && requestsTableRef.current) {
				// Find the first row's action button and click it
				setTimeout(() => {
					const tbody = requestsTableRef.current?.querySelector('tbody');
					if (tbody) {
						const firstRow = tbody.querySelector('tr:first-child');
						if (firstRow) {
							const cells = firstRow.querySelectorAll('td');
							if (cells.length > 0) {
								const actionCell = cells[cells.length - 1];
								const trigger = actionCell.querySelector(
									'button, [role="button"]',
								) as HTMLElement;
								if (trigger) {
									trigger.click();
								}
							}
						}
					}
				}, 300);
			}
		});

		return () => {
			tutorialActions.unregister('expandActionColumn');
		};
	}, [isRequestsTutorialActive, requestsTableRef]);

	// Helper function to register column references
	const registerColumnRefs = (
		thead: Element,
		registerElement: (key: TourElementKey, element: HTMLElement | null) => void,
	) => {
		const headers = thead.querySelectorAll('th');

		headers.forEach((header) => {
			const headerText = header.textContent?.trim() || '';
			if (headerText === 'Status' || headerText.includes('Status')) {
				registerElement('RequestsColumnStatusRef', header as HTMLElement);
			}
		});
	};

	// Register column refs - register immediately when tutorial is active and table is mounted
	useEffect(() => {
		// Only register if tutorial is active
		if (!isRequestsTutorialActive) {
			registerElement('RequestsColumnStatusRef', null);
			return;
		}

		const registerColumns = () => {
			if (!requestsTableRef.current) {
				registerElement('RequestsColumnStatusRef', null);
				return false; // Table not ready
			}

			const tableRoot = requestsTableRef.current;
			if (!tableRoot) return false;

			const thead = tableRoot.querySelector('thead');
			if (!thead) {
				return false; // Table not ready yet
			}

			// Check if table has rows (data is loaded)
			const tbody = tableRoot.querySelector('tbody');
			if (!tbody || tbody.querySelectorAll('tr').length === 0) {
				return false; // Data not loaded yet
			}

			registerColumnRefs(thead, registerElement);
			return true; // Successfully registered
		};

		// Try to register immediately
		let registered = registerColumns();
		
		// If not registered, try with intervals until successful or tutorial ends
		if (!registered) {
			const intervalId = setInterval(() => {
				if (!isRequestsTutorialActive) {
					clearInterval(intervalId);
					return;
				}
				
				registered = registerColumns();
				if (registered) {
					clearInterval(intervalId);
				}
			}, 100);

			// Clean up interval after 10 seconds to prevent infinite loops
			const timeoutId = setTimeout(() => {
				clearInterval(intervalId);
			}, 10000);

			return () => {
				clearInterval(intervalId);
				clearTimeout(timeoutId);
			};
		}
	}, [isRequestsTutorialActive, registerElement, requestsTableRef]);

	// Register refs for Requests tutorial elements
	useEffect(() => {
		if (!isRequestsTutorialActive) {
			// Clear all refs when tutorial is not active
			const refsToClear: TourElementKey[] = [
				'addButtonRequestsTableRef',
				'exportButtonRequestsTableRef',
				'OrdersActionHamburgerRef',
				'singleOrderButtonRef',
				'BulkOrderButtonRef',
			];
			refsToClear.forEach((ref) => {
				registerElement(ref, null);
			});
			return;
		}

		// Register refs with a delay to ensure DOM is ready
		const timeoutId = setTimeout(() => {
			if (requestsTableRef.current) {
				// Register Add Button ref - look for the add button
				const addButton = requestsTableRef.current.querySelector(
					'button[title*="Add"], button[aria-label*="Add"], button[title*="Create"]',
				) as HTMLElement;
				if (addButton) {
					registerElement('addButtonRequestsTableRef', addButton);
				}

				// Register Export Button ref - look for export button
				const exportButton = requestsTableRef.current.querySelector(
					'button[title*="Export"], button[aria-label*="Export"]',
				) as HTMLElement;
				if (exportButton) {
					registerElement('exportButtonRequestsTableRef', exportButton);
				}

			}
		}, 1000);

		return () => {
			clearTimeout(timeoutId);
		};
	}, [isRequestsTutorialActive, registerElement, requestsTableRef]);

	// Register menu button refs when menu is open
	useEffect(() => {
		if (!isRequestsTutorialActive) {
			registerElement('singleOrderButtonRef', null);
			registerElement('BulkOrderButtonRef', null);
			return;
		}

		const findAndRegisterMenuButtons = () => {
			const menuBox = findCreateMenuBox();
			
			if (!menuBox) {
				registerElement('singleOrderButtonRef', null);
				registerElement('BulkOrderButtonRef', null);
				return;
			}

			let { singleOrderButton, bulkOrderButton } = findMenuButtons(menuBox);
			
			if (!singleOrderButton || !bulkOrderButton) {
				const fallback = findMenuButtons(document);
				singleOrderButton = singleOrderButton || fallback.singleOrderButton;
				bulkOrderButton = bulkOrderButton || fallback.bulkOrderButton;
			}

			registerElement('singleOrderButtonRef', singleOrderButton);
			registerElement('BulkOrderButtonRef', bulkOrderButton);
		};

		const intervalId = setInterval(() => {
			if (!isRequestsTutorialActive) {
				clearInterval(intervalId);
				return;
			}
			findAndRegisterMenuButtons();
		}, 200);

		return () => {
			clearInterval(intervalId);
		};
	}, [isRequestsTutorialActive, registerElement]);

	// State to control if create menu should be forced open for tutorial
	const [shouldForceOpenCreateMenu, setShouldForceOpenCreateMenu] = useState(false);

	// Sync local state with module-level variable that DataTableFilters can access
	useEffect(() => {
		setForceOpenCreateMenuForTutorial(shouldForceOpenCreateMenu);
	}, [shouldForceOpenCreateMenu]);

	// Reset state when tutorial becomes inactive
	useEffect(() => {
		if (!isRequestsTutorialActive) {
			setShouldForceOpenCreateMenu(false);
		}
	}, [isRequestsTutorialActive]);

	useEffect(() => {
		// Action to open the Orders menu (add button menu)
		tutorialActions.register('OpenMenuOrders', () => {
			if (!isRequestsTutorialActive) return;
			setShouldForceOpenCreateMenu(true);
		});

		// Action to close the Orders menu
		tutorialActions.register('closeMenuOrders', () => {
			if (!isRequestsTutorialActive) return;
			setShouldForceOpenCreateMenu(false);
		});

		return () => {
			tutorialActions.unregister('OpenMenuOrders');
			tutorialActions.unregister('closeMenuOrders');
		};
	}, [isRequestsTutorialActive]);

	// Register the hamburger menu trigger for the first row after it renders
	useEffect(() => {
		if (!isRequestsTutorialActive) {
			registerElement('OrdersActionHamburgerRef', null);
			if (ordersActionHamburgerRef) {
				ordersActionHamburgerRef.current = null;
			}
			return;
		}

		// Helper function to find and register the action button
		const findAndRegisterHamburger = (): boolean => {
			// Check if table ref is available
			if (!requestsTableRef.current) {
				return false;
			}

			const tableRoot = requestsTableRef.current;
			if (!tableRoot) {
				return false;
			}

			// Find the tbody
			const tbody = tableRoot.querySelector('tbody');
			if (!tbody) {
				return false;
			}

			// Check if table has data rows
			const rows = tbody.querySelectorAll('tr');
			if (rows.length === 0) {
				return false;
			}

			// Find the first visible row (skip any hidden rows)
			let firstRow: Element | null = null;
			for (const row of Array.from(rows)) {
				const rowStyle = window.getComputedStyle(row as HTMLElement);
				if (
					rowStyle.display !== 'none' &&
					rowStyle.visibility !== 'hidden' &&
					(row as HTMLElement).offsetHeight > 0
				) {
					firstRow = row;
					break;
				}
			}

			if (!firstRow) {
				return false;
			}

			// Find the last cell (where the action menu is)
			const cells = firstRow.querySelectorAll('td');
			if (cells.length === 0) {
				return false;
			}

			// The action cell should be the last cell in the row
			const actionCell = cells[cells.length - 1];
			if (!actionCell) {
				return false;
			}
			
			// Verify that this cell actually contains an action menu (SVG or button)
			const svgInCell = actionCell.querySelector('svg');
			const roleButtonInCell = actionCell.querySelector('[role="button"]');
			const buttonInCell = actionCell.querySelector('button');
			const hasActionMenu = svgInCell || roleButtonInCell || buttonInCell;
			
			if (!hasActionMenu) {
				return false;
			}

			// Find the Box that contains the ActionMenuIcon (the actual clickable icon container)
			// First, find the SVG icon, then find its parent Box with display: flex
			let trigger: HTMLElement | null = null;
			const svgIcon = actionCell.querySelector('svg');
			
			if (svgIcon) {
				// Find the parent div that has display: flex and cursor: pointer
				let parent = svgIcon.parentElement;
				let depth = 0;
				while (parent && parent !== actionCell && parent !== tableRoot && depth < 10) {
					const style = window.getComputedStyle(parent);
					const isFlex = style.display === 'flex';
					const isPointer = style.cursor === 'pointer';
					
					if (isFlex && isPointer) {
						trigger = parent as HTMLElement;
						break;
					}
					parent = parent.parentElement;
					depth++;
				}
			}
			
			// Fallback: look for [role="button"] which is the CustomMenu.Trigger
			if (!trigger) {
				trigger = actionCell.querySelector('[role="button"]') as HTMLElement;
			}
			
			// Another fallback: look for button element
			if (!trigger) {
				trigger = actionCell.querySelector('button') as HTMLElement;
			}
			
			// Another fallback: look for any clickable element with cursor pointer
			if (!trigger) {
				const allClickable = actionCell.querySelectorAll('*');
				for (const el of Array.from(allClickable)) {
					const style = window.getComputedStyle(el as HTMLElement);
					if (style.cursor === 'pointer' && style.display !== 'none') {
						trigger = el as HTMLElement;
						break;
					}
				}
			}
			
			// Last fallback: the SVG icon itself
			if (!trigger && svgIcon) {
				trigger = svgIcon as unknown as HTMLElement;
			}

			if (trigger) {
				// Check if element is visible and has dimensions
				const rect = trigger.getBoundingClientRect();
				const style = window.getComputedStyle(trigger);
				const isVisible = rect.width > 0 &&
					rect.height > 0 &&
					style.display !== 'none' &&
					style.visibility !== 'hidden' &&
					trigger.offsetHeight > 0 &&
					trigger.offsetWidth > 0;
				
				if (isVisible) {
					// Update the ref so it can be used in useAppTour
					if (ordersActionHamburgerRef) {
						ordersActionHamburgerRef.current = trigger;
					}
					registerElement('OrdersActionHamburgerRef', trigger);
					return true;
				}
			}
			
			return false;
		};

		// Try immediately
		let found = findAndRegisterHamburger();
		
		// Timeout IDs for cleanup
		const timeoutIds: NodeJS.Timeout[] = [];
		
		// If not found, retry with multiple delays (similar to useReturnsTutorial)
		if (!found) {
			const timeout1 = setTimeout(() => {
				found = findAndRegisterHamburger();
				if (!found) {
					const timeout2 = setTimeout(() => {
						found = findAndRegisterHamburger();
						if (!found) {
							const timeout3 = setTimeout(() => {
								findAndRegisterHamburger();
							}, 1000);
							timeoutIds.push(timeout3);
						}
					}, 500);
					timeoutIds.push(timeout2);
				}
			}, 200);
			timeoutIds.push(timeout1);
		}

		
		const intervalId = setInterval(() => {
			if (!isRequestsTutorialActive) {
				clearInterval(intervalId);
				return;
			}

			if (!ordersActionHamburgerRef?.current) {
				const registered = findAndRegisterHamburger();
				if (registered) {
					clearInterval(intervalId);
				}
			}
		}, 1000);

		return () => {
			for (const id of timeoutIds) {
				clearTimeout(id);
			}
			clearInterval(intervalId);
			// Clear the ref when tutorial is not active
			if (ordersActionHamburgerRef) {
				ordersActionHamburgerRef.current = null;
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isRequestsTutorialActive, registerElement]);

	// All tutorial logic is handled internally via effects and action registrations
};


